import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ICommentsService } from "services/CommentsService";
import { CommentModel, CommentThreadRecipientModel, NewCommentRecipientModel } from "services/api/cplapi/CPLAPI";
import { CommentUpdateModel } from "services/classes/CommentUpdateModel";
import { NewCommentModel } from "services/classes/NewCommentModel";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

type CommentThreadState = {
  Comments: Array<CommentModel> | undefined;
  LoadingComments: boolean;
  EditingComments: boolean;
  EditingRecipients: boolean;
  Recipients: Array<CommentThreadRecipientModel> | undefined;
  LoadingRecipients: boolean;
};

const initialState: CommentThreadState = {
  Comments: new Array<CommentModel>(),
  LoadingComments: false,
  EditingComments: false,
  EditingRecipients: false,
  Recipients: new Array<CommentThreadRecipientModel>(),
  LoadingRecipients: false,
};

export const commentThreadSlice = createSlice({
  name: "commentThread",
  initialState,
  reducers: {
    resetData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getComments.pending, (state) => {
        state.LoadingComments = true;
      })
      .addCase(getComments.rejected, (state) => {
        state.LoadingComments = false;
      })
      .addCase(getComments.fulfilled, (state, action) => {
        state.LoadingComments = false;
        state.Comments = action.payload;
      })
      .addCase(getRecipients.pending, (state) => {
        state.LoadingRecipients = true;
      })
      .addCase(getRecipients.rejected, (state) => {
        state.LoadingRecipients = false;
      })
      .addCase(getRecipients.fulfilled, (state, action) => {
        state.LoadingRecipients = false;
        state.Recipients = action.payload;
      })
      .addCase(createComment.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(createComment.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(createComment.fulfilled, (state) => {
        state.EditingComments = false;
      })
      .addCase(updateComment.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(updateComment.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(updateComment.fulfilled, (state) => {
        state.EditingComments = false;
      })
      .addCase(makeCommentPublic.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(makeCommentPublic.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(makeCommentPublic.fulfilled, (state) => {
        state.EditingComments = false;
      })
      .addCase(deleteComment.pending, (state) => {
        state.EditingComments = true;
      })
      .addCase(deleteComment.rejected, (state) => {
        state.EditingComments = false;
      })
      .addCase(deleteComment.fulfilled, (state) => {
        state.EditingComments = false;
      })
      .addCase(deleteRecipient.pending, (state) => {
        state.EditingRecipients = true;
      })
      .addCase(deleteRecipient.rejected, (state) => {
        state.EditingRecipients = false;
      })
      .addCase(deleteRecipient.fulfilled, (state) => {
        state.EditingRecipients = false;
      })
      .addCase(addRecipient.pending, (state) => {
        state.EditingRecipients = true;
      })
      .addCase(addRecipient.rejected, (state) => {
        state.EditingRecipients = false;
      })
      .addCase(addRecipient.fulfilled, (state) => {
        state.EditingRecipients = false;
      });
  },
});

export const getComments = createAsyncThunk("CommentThread/GetComments", async (commentThreadID: string, thunkAPI) => {
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  return await commentsService.getComments(commentThreadID, thunkAPI.signal);
});

export const getRecipients = createAsyncThunk(
  "CommentThread/GetCommentThreadRecipients",
  async (commentThreadID: string) => {
    const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
    return await commentsService.getCommentThreadRecipients(commentThreadID);
  }
);

export const createComment = createAsyncThunk<
  void,
  { newCommentModel: NewCommentModel; commentThreadID: string },
  { state: RootState }
>("CommentThread/CreateComment", async ({ newCommentModel, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  await commentsService.createComment(newCommentModel);
  thunkAPI.dispatch(getComments(commentThreadID));
});

export const updateComment = createAsyncThunk<
  void,
  { commentUpdateModel: CommentUpdateModel; commentThreadID: string },
  { state: RootState }
>("CommentThread/UpdateComment", async ({ commentUpdateModel, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  await commentsService.updateComment(commentUpdateModel);
  thunkAPI.dispatch(getComments(commentThreadID));
});

export const makeCommentPublic = createAsyncThunk<
  void,
  { commentID: number; commentThreadID: string },
  { state: RootState }
>("CommentThread/MakeCommentPublic", async ({ commentID, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  await commentsService.makeCommentPublic(commentID);
  thunkAPI.dispatch(getComments(commentThreadID));
});

export const deleteComment = createAsyncThunk<
  void,
  { commentID: number; commentThreadID: string },
  { state: RootState }
>("CommentThread/DeleteComment", async ({ commentID, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  await commentsService.deleteComment(commentID);
  thunkAPI.dispatch(getComments(commentThreadID));
});

export const deleteRecipient = createAsyncThunk<
  void,
  { recipientID: number; commentThreadID: string },
  { state: RootState }
>("CommentThread/DeleteRecipient", async ({ recipientID, commentThreadID }, thunkAPI) => {
  const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
  await commentsService.deleteRecipient(recipientID);
  thunkAPI.dispatch(getRecipients(commentThreadID));
});

export const addRecipient = createAsyncThunk(
  "CommentThread/AddRecipient",
  async (values: NewCommentRecipientModel, thunkAPI) => {
    const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
    await commentsService.addRecipient(values);
    thunkAPI.dispatch(getRecipients(values.commentThreadID!));
  }
);

export const downloadCommentAttachment = createAsyncThunk(
  "CommentThread/DownloadCommentAttachment",
  async (commentAttachmentID: number) => {
    const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
    return await commentsService.downloadCommentAttachment(commentAttachmentID);
  }
);

export const updateCommentReadStatus = createAsyncThunk(
  "commentThread/updateReadStatus",
  async (payload: { commentID: number; commentThreadID: string }) => {
    const commentsService = container.get<ICommentsService>(SERVICE_TYPES.CommentsService);
    return await commentsService.updateCommentReadStatus(payload.commentID, payload.commentThreadID);
  }
);

export const { resetData } = commentThreadSlice.actions;

export const selectComments = (state: RootState) => state.commentThread.Comments;
export const selectLoadingComments = (state: RootState) => state.commentThread.LoadingComments;
export const selectEditingComments = (state: RootState) => state.commentThread.EditingComments;

export const selectRecipients = (state: RootState) => state.commentThread.Recipients;
export const selectLoadingRecipients = (state: RootState) => state.commentThread.LoadingRecipients;
export const selectEditingRecipients = (state: RootState) => state.commentThread.EditingRecipients;

export default commentThreadSlice.reducer;
